// Auto-extracted from Profile.jsx
import { useState, useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { C } from "../../lib/theme";
import { Card } from "../../components/Card";
import { Btn } from "../../components/Btn";
import { Eyebrow } from "../../components/Eyebrow";
import { Icon } from "../../components/Icon";
import { Skeleton } from "../../components/Skeleton";
import { EmptyState } from "../../components/EmptyState";
import { ProductCard } from "../../components/ProductCard";
import { supabase } from "../../lib/supabase";
import { fmtK } from "../../lib/money";
import { useWishlist } from "../../store/wishlistStore";
import { getAllLocations } from "../../data/zambianLocations";
import { timeAgo } from "../../lib/dateUtils";
import { useCategories } from "../../hooks/useCategories";
import { BecomeSellerModal } from "../../components/BecomeSellerModal";
import { PhoneVerificationModal } from "../../components/PhoneVerificationModal";
import { DeleteAccountModal } from "../../components/DeleteAccountModal";
import { Field, SecurityRow, InfoRow, inputStyle } from "./shared";
import { Toggle, SettingRow } from "./uiHelpers";

export function SettingsPanel({ user, goTo }) {
  const [notifOn, setNotifOn] = useState(true);
  const [trackingOn, setTrackingOn] = useState(false);
  const [defaultLocation, setDefaultLocation] = useState("—");
  const [savingNotif, setSavingNotif] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  useEffect(() => {
    try {
      setTrackingOn(localStorage.getItem("kapio_tracking_enabled") === "true");
    } catch { /* localStorage may be blocked */ }
    if (!user?.id) return;
    let cancelled = false;
    supabase
      .from("users")
      .select("settings, location_display, universities(short_name)")
      .eq("id", user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (cancelled || !data) return;
        if (data.settings?.push_notifications !== undefined) {
          setNotifOn(data.settings.push_notifications);
        }
        setDefaultLocation(data.universities?.short_name || data.location_display || "—");
      });
    return () => { cancelled = true; };
  }, [user?.id]);

  async function toggleNotif(val) {
    setNotifOn(val);
    setSavingNotif(true);
    const { data: curr } = await supabase
      .from("users")
      .select("settings")
      .eq("id", user.id)
      .maybeSingle();
    const merged = { ...(curr?.settings || {}), push_notifications: val };
    await supabase.from("users").update({ settings: merged }).eq("id", user.id);
    setSavingNotif(false);
  }

  function toggleTracking(val) {
    setTrackingOn(val);
    try { localStorage.setItem("kapio_tracking_enabled", String(val)); } catch { /* blocked */ }
  }

  return (
    <div style={{ display: "grid", gap: 14 }}>
      {/* Section 1: Preferences */}
      <Card pad={0}>
        <div style={{ padding: "16px 22px", borderBottom: `1px solid ${C.cream}` }}>
          <Eyebrow>Preferences</Eyebrow>
        </div>
        <SettingRow icon="bell" title="Push Notifications">
          <Toggle on={notifOn} onChange={toggleNotif} disabled={savingNotif} />
        </SettingRow>
        <SettingRow icon="moon" title="Theme">
          <span style={{ fontSize: 12, color: C.muted, fontWeight: 600 }}>Light</span>
        </SettingRow>
        <SettingRow icon="shield" title="Activity Tracking">
          <Toggle on={trackingOn} onChange={toggleTracking} />
        </SettingRow>
        <SettingRow icon="globe" title="Language">
          <span style={{ fontSize: 12, color: C.muted, fontWeight: 600 }}>English</span>
        </SettingRow>
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 22px" }}>
          <span style={{ width: 32, height: 32, borderRadius: 10, background: C.cream, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Icon name="map-pin" size={14} color={C.hunter} />
          </span>
          <div style={{ flex: 1, fontSize: 13, fontWeight: 700, color: C.charcoal }}>Default Location</div>
          <span style={{ fontSize: 12, color: C.muted, fontWeight: 600 }}>{defaultLocation}</span>
        </div>
      </Card>

      {/* Section 2: Security */}
      <Card pad={0}>
        <button
          onClick={() => goTo && goTo("security")}
          style={{ all: "unset", cursor: "pointer", display: "flex", alignItems: "center", gap: 12, padding: "14px 22px", width: "100%", boxSizing: "border-box" }}
        >
          <span style={{ width: 32, height: 32, borderRadius: 10, background: C.cream, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Icon name="key" size={14} color={C.muted} />
          </span>
          <span style={{ flex: 1, fontSize: 13, fontWeight: 700, color: C.charcoal }}>Change Password</span>
          <Icon name="chevron-right" size={14} color={C.muted} />
        </button>
      </Card>

      {/* Section 3: Legal + About */}
      <Card pad={0}>
        <a
          href="https://kapio-apps.pages.dev/privacy.html"
          target="_blank"
          rel="noopener noreferrer"
          style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 22px", textDecoration: "none", color: "inherit", borderBottom: `1px solid ${C.cream}` }}
        >
          <span style={{ width: 32, height: 32, borderRadius: 10, background: C.cream, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Icon name="shield" size={14} color={C.hunter} />
          </span>
          <span style={{ flex: 1, fontSize: 13, fontWeight: 700, color: C.charcoal }}>Privacy Policy</span>
          <Icon name="arrow-right" size={14} color={C.muted} />
        </a>
        <a
          href="https://kapio-apps.pages.dev/terms.html"
          target="_blank"
          rel="noopener noreferrer"
          style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 22px", textDecoration: "none", color: "inherit", borderBottom: `1px solid ${C.cream}` }}
        >
          <span style={{ width: 32, height: 32, borderRadius: 10, background: C.cream, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Icon name="file-text" size={14} color={C.hunter} />
          </span>
          <span style={{ flex: 1, fontSize: 13, fontWeight: 700, color: C.charcoal }}>Terms of Service</span>
          <Icon name="arrow-right" size={14} color={C.muted} />
        </a>
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 22px" }}>
          <span style={{ width: 32, height: 32, borderRadius: 10, background: C.cream, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Icon name="info" size={14} color={C.hunter} />
          </span>
          <span style={{ flex: 1, fontSize: 13, fontWeight: 700, color: C.charcoal }}>About Kapio</span>
          <span style={{ fontSize: 12, color: C.muted, fontWeight: 600 }}>v1.0</span>
        </div>
      </Card>

      {/* Delete account — opens the same wizard as Privacy & Security */}
      <button
        onClick={() => setDeleteOpen(true)}
        style={{
          all: "unset",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "14px 22px",
          borderRadius: 18,
          background: C.redBg,
          border: `1px solid ${C.red}33`,
        }}
      >
        <Icon name="trash-2" size={16} color={C.red} />
        <span style={{ flex: 1, fontSize: 13, fontWeight: 700, color: C.red }}>Delete My Account</span>
        <Icon name="chevron-right" size={14} color={C.red} />
      </button>

      <DeleteAccountModal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        user={user}
      />
    </div>
  );
}

// =============================================================================
// SUPPORT
// =============================================================================
